import React, { useContext, useMemo } from 'react'
import { pluralizeToString } from 'lib/string/pluralize'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE } from 'constants/offer'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import GeoContext from 'contexts/geoContext'
import getLPCRegionLOS from 'lib/offer/getLPCRegionLOS'
import Group from 'components/utils/Group'
import TextButton from 'components/Luxkit/Button/TextButton'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import WalledContent from 'components/Common/WalledContent'
import OfferTileLoggedInButtons from 'components/OfferList/OfferListTiles/Support/OfferTileLoggedInButtons'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import LuxuryPlusPriceGuard from 'loyalty/components/LuxuryPlusPriceGuard'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceStackLoyaltyProminentPrice from 'components/Luxkit/PricePoints/Loyalty/PriceStackLoyaltyProminentPrice'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import ProductPaletteProvider, { ProductPalette } from 'contexts/ProductPaletteContext'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import FrontPageTileFlightPricing from '../OfferListTiles/Support/FrontPageTileFlightPricing'
import { isUltraLuxOffer } from 'lib/offer/offerUtils'

interface Props {
  offer: App.Offer | App.OfferSummary;
}

function LEOfferPriceDetails(props: Props) {
  const {
    offer,
  } = props

  const pkg = offer.lowestPricePackage!
  const { isTaxExclusiveRegion } = useContext(GeoContext)
  const propertyFees = pkg.propertyFees ?? 0

  const totalPrice = isTaxExclusiveRegion ? pkg.price : pkg.price + propertyFees
  const totalMemberPrice = pkg.memberPrice > 0 ? (isTaxExclusiveRegion ? pkg.memberPrice : pkg.memberPrice + propertyFees) : 0
  const totalValue = isTaxExclusiveRegion ? pkg.value : pkg.value + propertyFees

  const valueOffPercent = getValueOffPercent(totalValue, (totalMemberPrice || totalPrice))
  const shouldShowRate = !offer.isDiscountPillHidden && valueOffPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const totalMemberPriceOrTotalPrice = totalMemberPrice || totalPrice

  const shouldShowDiscountPercent = offer.type === OFFER_TYPE_LAST_MINUTE
  const shouldShowValue = pkg.value > 0 && totalValue > totalMemberPriceOrTotalPrice && pkg.shouldDisplayValue &&
    !shouldShowDiscountPercent && (offer.type == OFFER_TYPE_HOTEL || !pkg.roomRate?.inclusionsHideValue)

  let staysFrom = offer.minDuration
  const LPCRegionLOS = getLPCRegionLOS(offer)
  if (LPCRegionLOS && offer.packages.some(p => p.duration === LPCRegionLOS)) {
    staysFrom = LPCRegionLOS
  }
  const showStaysFromLabel = staysFrom && offer.type === OFFER_TYPE_ALWAYS_ON && LPCRegionLOS && staysFrom < pkg.duration

  const durationText = useMemo(() => {
    if (offer.type === 'rental' && offer.lowestPricePackage) {
      return `${offer.lowestPricePackage.durationLabel.toLocaleLowerCase()} from`
    }

    if (offer.tileDurationLabel) {
      return `${offer.tileDurationLabel.toLocaleLowerCase()} from`
    }
    if (LPCRegionLOS) {
      return `${pkg.duration} nights from`
    }
    return 'From'
  }, [LPCRegionLOS, offer.lowestPricePackage, offer.tileDurationLabel, offer.type, pkg.duration])

  const wall = <OfferTileLoggedInButtons
    signUpText="Sign up for free"
    align="right"
    fullWidth={false}
    discountLabelAlign="end"
  />

  const paletteValue: ProductPalette = offer.loyalty?.earlyAccess ? 'loyalty' : isUltraLuxOffer(offer) ? 'ultralux' : 'default'
  return (
    <WalledContent enforced={offer.walledGarden} wall={wall}>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
        <VerticalSpacer gap={8}>
          <Group direction="vertical">
            <PriceRowPriceCaption>{durationText}</PriceRowPriceCaption>
            <LuxuryPlusPriceGuard
              showMemberPrice={pkg.memberPrice > 0}
              fallbackPrice={<>
                <PriceRowPrice
                  size="L"
                  price={totalPrice}
                  saleUnit={offer.saleUnit}
                  taxesAndFees={pkg.taxesAndFees}
                />
                <PriceRowTaxesAndFees
                  isTaxExclusiveRegion={offer.property?.taxesPayableAtProperty}
                  taxesAndFees={pkg.taxesAndFees}
                />
              </>}
            >
              <PriceStackLoyaltyProminentPrice
                memberPrice={totalMemberPrice}
                saleUnit={offer.saleUnit}
                taxesAndFees={pkg.taxesAndFees}
                nonMemberPrice={totalPrice}
                size="L"
                orientation="vertical"
                horizontalAlign="start"
              />
            </LuxuryPlusPriceGuard>
            {shouldShowValue && <PriceRowValueDiscountWithCaption
              size="L"
              value={Math.ceil(totalValue)}
              discountPercentage={shouldShowRate ? valueOffPercent : undefined}
            />}
            {staysFrom && showStaysFromLabel && <PriceRowCaption>
              Stays from {pluralizeToString('night', staysFrom)} available
            </PriceRowCaption>}
            {!!pkg.roomOccupancy && offer.type === 'tour' && <PriceRowCaption>
              {pkg.roomOccupancy}
            </PriceRowCaption>}
          </Group>
          <PriceRowAgentHubCommission
            size="L"
            // ultralux isn't supported yet, use it's base type instead
            productType={offer.productType === 'ultralux_hotel' ? offer.type : offer.productType}
          />
          <FrontPageTileFlightPricing offer={offer} />
        </VerticalSpacer>
        <CSSBreakpoint min="desktop">
          <ProductPaletteProvider palette={paletteValue}>
            <TextButton kind="primary" nonInteractable>
              View
            </TextButton>
          </ProductPaletteProvider>
        </CSSBreakpoint>
      </Group>
    </WalledContent>
  )
}

export default LEOfferPriceDetails
